'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface CheckoutFormProps {
  onSubmit: (formData: FormData) => void;
  isProcessing: boolean;
}

interface FormData {
  fullName: string;
  email: string;
  phone: string;
  vehicleNumber: string;
  address: string;
  city: string;
  state: string;
  pincode: string;
  paymentMethod: string;
}

interface FormErrors {
  [key: string]: string;
}

const indianStates = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
  'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
  'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Delhi', 'Puducherry', 'Jammu and Kashmir', 'Ladakh'
];

export default function CheckoutForm({ onSubmit, isProcessing }: CheckoutFormProps) {
  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    email: '',
    phone: '',
    vehicleNumber: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    paymentMethod: 'upi'
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [currentStep, setCurrentStep] = useState<number>(1);

  const validateStep = (step: number): boolean => {
    const newErrors: FormErrors = {};

    if (step === 1) {
      if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required';
      if (!formData.email.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Invalid email format';
      }
      if (!formData.phone.trim()) {
        newErrors.phone = 'Phone number is required';
      } else if (!/^[6-9]\d{9}$/.test(formData.phone)) {
        newErrors.phone = 'Invalid Indian phone number';
      }
      if (!formData.vehicleNumber.trim()) {
        newErrors.vehicleNumber = 'Vehicle number is required';
      }
    }

    if (step === 2) {
      if (!formData.address.trim()) newErrors.address = 'Address is required';
      if (!formData.city.trim()) newErrors.city = 'City is required';
      if (!formData.state.trim()) newErrors.state = 'State is required';
      if (!formData.pincode.trim()) {
        newErrors.pincode = 'Pincode is required';
      } else if (!/^\d{6}$/.test(formData.pincode)) {
        newErrors.pincode = 'Invalid pincode format';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    setCurrentStep(currentStep - 1);
    setErrors({});
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateStep(2)) {
      onSubmit(formData);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  return (
    <div className="space-y-8">
      {/* Progress Steps */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4 flex-1">
          <div className={`flex items-center justify-center w-10 h-10 rounded-full text-white font-semibold transition-all ${
            currentStep >= 1 ? 'bg-accent shadow-button' : 'bg-muted text-muted-foreground'
          }`}>
            {currentStep > 1 ? <Icon name="CheckIcon" size={20} /> : '1'}
          </div>
          <div className={`flex-1 h-1 transition-all ${
            currentStep >= 2 ? 'bg-accent' : 'bg-border'
          }`} />
          <div className={`flex items-center justify-center w-10 h-10 rounded-full text-white font-semibold transition-all ${
            currentStep >= 2 ? 'bg-accent shadow-button' : 'bg-muted text-muted-foreground'
          }`}>
            {currentStep > 2 ? <Icon name="CheckIcon" size={20} /> : '2'}
          </div>
          <div className={`flex-1 h-1 transition-all ${
            currentStep >= 3 ? 'bg-accent' : 'bg-border'
          }`} />
          <div className={`flex items-center justify-center w-10 h-10 rounded-full text-white font-semibold transition-all ${
            currentStep >= 3 ? 'bg-accent shadow-button' : 'bg-muted text-muted-foreground'
          }`}>
            3
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Step 1: Personal Details */}
        {currentStep === 1 && (
          <div className="space-y-6">
            <h3 className="text-2xl font-heading font-bold text-gray-900 mb-6">Your Details</h3>
            
            <div>
              <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-2">
                Full Name (as per ID) *
              </label>
              <input
                type="text"
                id="fullName"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                className={`w-full px-5 py-3.5 border rounded-xl bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent/40 transition-all ${
                  errors.fullName ? 'border-red-400' : 'border-gray-200'
                }`}
                placeholder="Enter your full legal name"
              />
              {errors.fullName && (
                <p className="mt-2 text-xs text-red-600 flex items-center gap-1.5">
                  <Icon name="ExclamationCircleIcon" size={14} />
                  {errors.fullName}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address (for order updates) *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`w-full px-5 py-3.5 border rounded-xl bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent/40 transition-all ${
                  errors.email ? 'border-red-400' : 'border-gray-200'
                }`}
                placeholder="you@example.com"
              />
              {errors.email && (
                <p className="mt-2 text-xs text-red-600 flex items-center gap-1.5">
                  <Icon name="ExclamationCircleIcon" size={14} />
                  {errors.email}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                Mobile Number (linked with ParkPing) *
              </label>
              <div className="flex gap-2">
                <div className="w-20 px-4 py-3.5 border border-gray-200 rounded-xl bg-gray-50 text-gray-600 flex items-center justify-center font-medium">
                  +91
                </div>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  maxLength={10}
                  className={`flex-1 px-5 py-3.5 border rounded-xl bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent/40 transition-all ${
                    errors.phone ? 'border-red-400' : 'border-gray-200'
                  }`}
                  placeholder="Enter 10-digit mobile number"
                />
              </div>
              {errors.phone && (
                <p className="mt-2 text-xs text-red-600 flex items-center gap-1.5">
                  <Icon name="ExclamationCircleIcon" size={14} />
                  {errors.phone}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="vehicleNumber" className="block text-sm font-medium text-gray-700 mb-2">
                Vehicle Number *
              </label>
              <input
                type="text"
                id="vehicleNumber"
                name="vehicleNumber"
                value={formData.vehicleNumber}
                onChange={handleChange}
                className={`w-full px-5 py-3.5 border rounded-xl bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent/40 transition-all ${
                  errors.vehicleNumber ? 'border-red-400' : 'border-gray-200'
                }`}
                placeholder="e.g., DL-01-AB-1234"
              />
              {errors.vehicleNumber && (
                <p className="mt-2 text-xs text-red-600 flex items-center gap-1.5">
                  <Icon name="ExclamationCircleIcon" size={14} />
                  {errors.vehicleNumber}
                </p>
              )}
            </div>

            <button
              type="button"
              onClick={handleNext}
              className="w-full px-8 py-4 bg-accent text-accent-foreground font-heading font-semibold rounded-xl shadow-cta hover:shadow-cta-hover hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Continue to Address
            </button>
          </div>
        )}

        {/* Step 2: Delivery Details */}
        {currentStep === 2 && (
          <div className="space-y-6">
            <h3 className="text-2xl font-heading font-extrabold text-foreground mb-2">Delivery address</h3>
            <p className="text-sm text-muted-foreground -mt-2">
              Used only to ship your ParkPing card(s).
            </p>
            
            <div>
              <label htmlFor="address" className="block text-sm font-medium text-foreground mb-2">
                Complete Address *
              </label>
              <textarea
                id="address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                rows={3}
                className={`w-full px-5 py-3.5 border rounded-xl bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent/40 transition-all resize-none ${
                  errors.address ? 'border-red-400' : 'border-gray-200'
                }`}
                placeholder="House/Flat No., Building Name, Street, Landmark"
              />
              {errors.address && (
                <p className="mt-2 text-xs text-red-600 flex items-center gap-1.5">
                  <Icon name="ExclamationCircleIcon" size={14} />
                  {errors.address}
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="city" className="block text-sm font-medium text-foreground mb-2">
                  City *
                </label>
                <input
                  type="text"
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  className={`w-full px-5 py-3.5 border rounded-xl bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent/40 transition-all ${
                    errors.city ? 'border-red-400' : 'border-gray-200'
                  }`}
                  placeholder="Enter your city"
                />
                {errors.city && (
                  <p className="mt-2 text-xs text-red-600 flex items-center gap-1.5">
                    <Icon name="ExclamationCircleIcon" size={14} />
                    {errors.city}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="pincode" className="block text-sm font-medium text-foreground mb-2">
                  Pincode *
                </label>
                <input
                  type="text"
                  id="pincode"
                  name="pincode"
                  value={formData.pincode}
                  onChange={handleChange}
                  maxLength={6}
                  className={`w-full px-5 py-3.5 border rounded-xl bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent/40 transition-all ${
                    errors.pincode ? 'border-red-400' : 'border-gray-200'
                  }`}
                  placeholder="6-digit pincode"
                />
                {errors.pincode && (
                  <p className="mt-2 text-xs text-red-600 flex items-center gap-1.5">
                    <Icon name="ExclamationCircleIcon" size={14} />
                    {errors.pincode}
                  </p>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="state" className="block text-sm font-medium text-foreground mb-2">
                State *
              </label>
              <select
                id="state"
                name="state"
                value={formData.state}
                onChange={handleChange}
                className={`w-full px-5 py-3.5 border rounded-xl bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent/40 transition-all ${
                  errors.state ? 'border-red-400' : 'border-gray-200'
                }`}
              >
                <option value="">Select State</option>
                {indianStates.map(state => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </select>
              {errors.state && (
                <p className="mt-2 text-xs text-red-600 flex items-center gap-1.5">
                  <Icon name="ExclamationCircleIcon" size={14} />
                  {errors.state}
                </p>
              )}
            </div>

            <div className="flex gap-4">
              <button
                type="button"
                onClick={handleBack}
                disabled={isProcessing}
                className="flex-1 px-8 py-4 border border-border text-foreground font-heading font-semibold rounded-xl hover:bg-muted/30 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Back
              </button>
              <button
                type="button"
                onClick={handleNext}
                disabled={isProcessing}
                className="flex-1 px-8 py-4 bg-accent text-accent-foreground font-heading font-semibold rounded-xl shadow-cta hover:shadow-cta-hover hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Proceed to Payment
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Payment Method */}
        {currentStep === 3 && (
          <div className="space-y-6">
            <h3 className="text-2xl font-heading font-extrabold text-foreground mb-2">Payment method</h3>
            <p className="text-sm text-muted-foreground -mt-2">
              Pay securely. We never store your UPI or card details.
            </p>
            
            <div className="space-y-4">
              <label className={`flex items-center gap-4 p-5 border rounded-2xl cursor-pointer transition-all duration-200 ${
                formData.paymentMethod === 'upi' ? 'border-accent bg-accent/5 shadow-subtle' : 'border-border bg-background hover:border-accent/30'
              }`}>
                <input
                  type="radio"
                  name="paymentMethod"
                  value="upi"
                  checked={formData.paymentMethod === 'upi'}
                  onChange={handleChange}
                  className="w-5 h-5 text-accent"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Icon name="DevicePhoneMobileIcon" size={20} className="text-accent" />
                    <span className="font-medium text-foreground">UPI</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Fast and secure (GPay, PhonePe, Paytm, BHIM)</p>
                </div>
              </label>

              <label className={`flex items-center gap-4 p-5 border rounded-2xl cursor-pointer transition-all duration-200 ${
                formData.paymentMethod === 'card' ? 'border-accent bg-accent/5 shadow-subtle' : 'border-border bg-background hover:border-accent/30'
              }`}>
                <input
                  type="radio"
                  name="paymentMethod"
                  value="card"
                  checked={formData.paymentMethod === 'card'}
                  onChange={handleChange}
                  className="w-5 h-5 text-accent"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Icon name="CreditCardIcon" size={20} className="text-accent" />
                    <span className="font-medium text-foreground">Credit/Debit card</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Visa, Mastercard, RuPay, Amex</p>
                </div>
              </label>

              <label className={`flex items-center gap-4 p-5 border rounded-2xl cursor-pointer transition-all duration-200 ${
                formData.paymentMethod === 'netbanking' ? 'border-accent bg-accent/5 shadow-subtle' : 'border-border bg-background hover:border-accent/30'
              }`}>
                <input
                  type="radio"
                  name="paymentMethod"
                  value="netbanking"
                  checked={formData.paymentMethod === 'netbanking'}
                  onChange={handleChange}
                  className="w-5 h-5 text-accent"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Icon name="BuildingLibraryIcon" size={20} className="text-accent" />
                    <span className="font-medium text-foreground">Net banking</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Instant payment via 100+ Indian banks</p>
                </div>
              </label>

              <label className={`flex items-center gap-4 p-5 border rounded-2xl cursor-pointer transition-all duration-200 ${
                formData.paymentMethod === 'cod' ? 'border-accent bg-accent/5 shadow-subtle' : 'border-border bg-background hover:border-accent/30'
              }`}>
                <input
                  type="radio"
                  name="paymentMethod"
                  value="cod"
                  checked={formData.paymentMethod === 'cod'}
                  onChange={handleChange}
                  className="w-5 h-5 text-accent"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Icon name="BanknotesIcon" size={20} className="text-accent" />
                    <span className="font-medium text-foreground">Cash on delivery</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Pay at doorstep on delivery</p>
                </div>
              </label>
            </div>

            <div className="bg-muted/20 rounded-2xl p-5 border border-border">
              <div className="flex items-start gap-3">
                <Icon name="ShieldCheckIcon" size={20} className="text-accent flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-foreground">Secure payment</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Payments are encrypted. We never store your card or UPI details.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-4">
              <button
                type="button"
                onClick={handleBack}
                disabled={isProcessing}
                className="flex-1 px-8 py-4 border border-border text-foreground font-heading font-semibold rounded-xl hover:bg-muted/30 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={isProcessing}
                className="flex-1 px-8 py-4 bg-accent text-accent-foreground font-heading font-semibold rounded-xl shadow-cta hover:shadow-cta-hover hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isProcessing ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Icon name="LockClosedIcon" size={20} />
                    Place Secure Order
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </form>
    </div>
  );
}
